import { atom, selector, useRecoilValueLoadable } from 'recoil'
import { AccountDetailInfoVO, ShopInfoVo, ShopDetailVo, PassportUserVo, BrandInfoVo, SettledShopListVo, ShopAndBrandInfoVoRes } from '@/@types'
import { getLoginShopid, getLoginShopInfo, getLoginBrandid, getLoginBrandInfo, hasBRbacPermission, getMerchantMode, MerchantMode, getShopSelectorMode, getShopIDs } from '@RootCommon/utils'
import { DEFAULT_PASSPORT_USERINFO, AUTH_MENU_KEY, DEFAULT_MERCHANT_USERINFO } from '@/common/constants'
import { commonRequest } from '@/common/api'
import { isGroupOrBrandAcct } from '@RootCommon/utils'
import { ShopSelectorType } from '@/common/types'
const storedShopId = getLoginShopid() || 0
let shopInfo = getLoginShopInfo() || {}
let storedBrandId = getLoginBrandid() || '0'
let brandInfo = getLoginBrandInfo() || {}


let isSingleShopMode = getMerchantMode() === 'singleStore'
// url上有门店id,优先切换到url的门店。与当前会话的门店id不一致，则清空现有门店、品牌信息
if (shopInfo.shopId !== storedShopId || String(shopInfo.shopId) === '-1') {
  // 门店异常置-1后，刷新可恢复
  shopInfo = {}
  storedBrandId = '0'
  brandInfo = {}
}

export const brandIdAtom = atom({
  key: 'brandIdAtom',
  default: storedBrandId,
})

// 全局门店信息
export const shopIdAtom = atom({
  key: 'shopIdAtom',
  default: String(storedShopId) === '-1' ? 0 : storedShopId,
})
export const shopInfoAtom = atom({
  key: 'shopInfoAtom',
  default: shopInfo as ShopDetailVo,
})
export const brandInfoAtom = atom({
  key: 'brandInfoAtom',
  default: brandInfo as BrandInfoVo,
})

// 全局菜单信息
export const showMenusAtom = atom({
  key: 'showMenusAtom',
  default: [],
})

// 全局商家账号信息
export const accountDetailInfoAtom = atom({
  key: 'accountDetailInfo',
  default: DEFAULT_MERCHANT_USERINFO as AccountDetailInfoVO,
})

// 全局账号信息
export const passportUserInfoAtom = atom({
  key: 'passportUserInfo',
  default: DEFAULT_PASSPORT_USERINFO as PassportUserVo,
})

export const ignoreMenuListAtom = atom({
  key: 'ignoreMenuList',
  default: [
    AUTH_MENU_KEY.GROWTH, // 默认是隐藏的，如果不是再通过接口移除
    AUTH_MENU_KEY.HEALTHY, // 默认是隐藏的，如果不是再通过接口移除
    AUTH_MENU_KEY.NEWORDER, // 默认是隐藏的，如果不是再通过接口移除
    AUTH_MENU_KEY.SUBACCOUNT, // 子账号，默认是隐藏的，符合的账号类型才展示
    AUTH_MENU_KEY.MERCHANT_MARKET, // 商家商城灰度
  ] as string[],
})

export const shopListAtom = atom({
  key: 'shopListAtom',
  default: [] as ShopInfoVo[],
})

export const brandListAtom = atom({
  key: 'brandListAtom',
  default: [] as BrandInfoVo[],
})

// 全局账号、门店信息加载完成，子系统可渲染标记
export const accountInfoLoadFinishAtom = atom({
  key: 'accountInfoLoadFinishAtom',
  default: false,
})

export const sideMenuAdInfoAtom = atom({
  key: 'sideMenuAdInfoAtom',
  default: {} as any,
})

// 顶部消息铃铛右上角标记
export const headerMessageDotAtom = atom({
  key: 'headerMessageDotAtom',
  default: false,
})

// 全局权限灰度开关
export const rbacAuthGrayAtom = atom({
  key: 'rbacAuthGrayAtom',
  default: false,
})

// 全局权限code
export const rbacAuthResourcesAtom = atom({
  key: 'rbacAuthResourcesAtom',
  default: [],
})

// 门店账号信息
export const shopInfoFinishedAtom = atom({
  key: 'shopInfoFinishedAtom',
  default: false,
})

// 消息红点状态
export const messageCenterDotAtom = atom({
  key: 'messageCenterDotAtom',
  default: false,
})

const isBrandHome = selector({
  key: 'isBrandHome',
  get: async () => {
    try {
      // 通过左侧菜单是否展示首页来间接控制白名单
      return isGroupOrBrandAcct?.(undefined) && hasBRbacPermission('PC_HOME')
    } catch (e) {
      console.error(e)
      return false
    }
  },
})

// 获取是否是品牌首页
export const useIsBrandHome = () => {
  const loadable = useRecoilValueLoadable(isBrandHome)
  return loadable.state === 'hasValue'
    ? {
      hasValue: true,
      content: loadable.contents,
    }
    : {
      hasValue: false,
      content: false,
    }
}

// 自入驻门店列表，用于自入驻门店列表的展示，非营业中门店
export const settledShopListAtom = atom({
  key: 'settledShopListAtom',
  default: [] as SettledShopListVo[],
})

// IM入口展示判断接口
export const imEntryShowAtom = atom({
  key: 'imEntryShowAtom',
  default: false,
})

// 是否单店模式
export const merchantModeAtom = atom<MerchantMode>({
  key: 'merchantModeAtom',
  default: getMerchantMode(),
})

/** 门店选择器模式 */
export const shopSelectorModeAtom = atom<ShopSelectorType>({
  key: 'shopSelectorMode',
  default: getShopSelectorMode(),
})

/** 门店、品牌、客户分组、品牌分组信息 */
export const shopAndAccountInfoAtom = atom({
  key: 'shopAndAccountInfoAtom',
  default: {
    shopInfoList: [],
    brandInfoList: [],
    customerGroupInfoList: [],
    brandGroupInfoList: [],
  } as ShopAndBrandInfoVoRes,
})


/** 选中门店id列表 */
export const selectShopIdsAtom = atom<string[]>({
  key: 'selectShopIdsAtom',
  default: getShopIDs() as string[],
})


/** 门店选择器，选择了单选门店的id */
export const singleSelectedAtom = atom<string>({
  key: 'singleSelectedAtom',
  default: String(getLoginShopid()) || '0',
})

/** 是否是有效账号 */
export const isValidAccountAtom = atom<boolean>({
  key: 'isValidAccountAtom',
  default: true,
})

/** Agent 弹窗是否打开 */
export const agentDrawerVisibleAtom = atom<boolean>({
  key: 'agentDrawerVisibleAtom',
  default: false,
})

/** Agent deeplink 透传参数（targetShopId、cid 等），供 AgentPanel 拼 iframe url */
export const agentDeeplinkParamsAtom = atom<Record<string, string>>({
  key: 'agentDeeplinkParamsAtom',
  default: {},
})
